import React, { useState, useEffect, useRef, useContext } from 'react';
import { AppContext } from '../context/AppContext';
import { useTranslation } from 'react-i18next';
import { ScanBarcode, AlertCircle } from 'lucide-react';

const CheckPrice = () => {
    const { authFetch } = useContext(AppContext);
    const { t } = useTranslation();
    const [barcode, setBarcode] = useState('');
    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const inputRef = useRef(null);

    // Keep input focused automatically
    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus();
        }
    }, [product, error]);

    // Also refocus if clicking anywhere on the background
    const handleBackgroundClick = () => {
        if (inputRef.current) {
            inputRef.current.focus();
        }
    };

    const handleScan = async (e) => {
        e.preventDefault();
        const trimmedBarcode = barcode.trim();
        if (!trimmedBarcode) return;

        setLoading(true);
        setError('');
        setProduct(null);

        try {
            const res = await authFetch(`/api/products?search=${encodeURIComponent(trimmedBarcode)}`);
            if (!res.ok) throw new Error('Failed to fetch');
            const data = await res.json();
            
            if (data && data.length > 0) {
                const exactMatch = data.find(p => p.barcode === trimmedBarcode || p.manual_barcode === trimmedBarcode || p.sku === trimmedBarcode);
                setProduct(exactMatch || data[0]);
            } else {
                setError(t('Product not found'));
            }
        } catch (err) {
            setError(t('Error searching product'));
        } finally {
            setLoading(false);
            setBarcode('');
        }
    };

    return (
        <div className="flex flex-col min-h-[80vh] justify-center items-center p-4" onClick={handleBackgroundClick}>
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 w-full max-w-lg p-8" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-center gap-2 mb-6">
                    <ScanBarcode className="w-6 h-6 text-indigo-600" />
                    <h2 className="text-xl font-bold text-gray-900">
                        {t('Price Check')}
                    </h2>
                </div>

                <form onSubmit={handleScan} className="mb-6 relative">
                    <input
                        ref={inputRef}
                        type="text"
                        value={barcode}
                        onChange={(e) => setBarcode(e.target.value)}
                        placeholder={t('Scan barcode or enter code...')}
                        className="w-full text-center text-xl p-3.5 border border-gray-300 rounded-lg focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none transition"
                        autoFocus
                        disabled={loading}
                    />
                    {loading && (
                        <div className="absolute right-4 top-1/2 -translate-y-1/2">
                            <div className="w-5 h-5 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
                        </div>
                    )}
                </form>

                <div className="min-h-[180px] flex flex-col justify-center items-center bg-gray-50 rounded-lg p-6 border border-gray-100">
                    {product ? (
                        <div className="text-center">
                            <h3 className="text-2xl font-bold text-gray-900 mb-3">{product.name}</h3>
                            <div className="text-4xl font-extrabold text-indigo-600 mb-2">
                                {parseFloat(product.price_ttc || product.price || 0).toFixed(2)} CFA
                            </div>
                            {(product.price_ht || product.price) && (
                                <div className="text-xs text-gray-500 font-medium bg-gray-200/70 px-3 py-1 rounded-md inline-block">
                                    HT: {parseFloat(product.price_ht || product.price || 0).toFixed(2)} CFA
                                </div>
                            )}
                        </div>
                    ) : error ? (
                        <div className="text-center text-rose-600">
                            <AlertCircle className="w-10 h-10 mx-auto mb-2 text-rose-500" />
                            <p className="font-semibold text-sm">{error}</p>
                        </div>
                    ) : (
                        <div className="text-center text-gray-400">
                            <ScanBarcode className="w-12 h-12 mx-auto mb-2 opacity-40" />
                            <p className="text-sm font-medium">{t('Ready to scan barcode')}</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CheckPrice;
